import { computed, ref, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import MenusService from '@/api/services/menus.service';
import { useMenu } from '@/composition/use-menu';
import {
  countCatalogFilters,
  ICatalogFilterRow,
  TCatalogFilter,
} from '@/domain/catalog/catalog-filters';
import {
  buildSearchIndex,
  searchMatcher,
} from '@/domain/catalog/catalog-search';
import {
  filterSections,
  groupCatalog,
  ICatalogRowContext,
  ICatalogSection,
  IShownSection,
  shareSections,
} from '@/domain/catalog/catalog-view';
import { splitTerms } from '@/domain/pos/search-normalize';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { IBranch } from '@/interfaces/IBranch';
import { IMenu } from '@/interfaces/IMenu';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { vueData } from '@/VueData';

/** Search waits this long after the last keystroke (plan §5.5). */
export const SEARCH_DEBOUNCE_MS = 300;

export interface ICatalogDataState {
  menuId: number | null;
  filter: TCatalogFilter;
  query: string;
  pinnedIds: Record<number, true>;
}

/**
 * The Menú page's derived catalog (plan §5.6): the current menu's sections
 * from the shared menu store, the filtered and searched view, chip counts
 * and the store's menus. Everything is memoised on the store's objects, so
 * a realtime upsert only rebuilds what depends on the changed rows.
 */
export function useCatalogData(state: ICatalogDataState) {
  const menu = useMenu();
  const restaurantStore = useRestaurantStore();

  const restaurant = computed(() => restaurantStore.restaurant);
  const branchId = computed(() => restaurantStore.selectedBranch?.id ?? null);
  /**
   * Every branch of the store (the store's own list only holds the role's
   * branches): multi-branch rules and «Sucursales» need them all.
   */
  const storeBranches = computed<IBranch[]>(
    () => vueData.allBranches ?? restaurantStore.branches ?? []
  );
  const activeStoreBranches = computed(() =>
    storeBranches.value.filter((branch) => branch.active)
  );
  const multipleMenus = computed(
    () => !!restaurantStore.restaurantSettings?.multipleMenus
  );

  /** The store's live menus other than the base one (multi-menu stores). */
  const menus = ref<IMenu[]>([]);
  const menusLoaded = ref(false);
  async function loadMenus() {
    const restaurantId = restaurant.value?.id;
    if (!restaurantId || !multipleMenus.value) {
      menus.value = [];
      menusLoaded.value = true;
      return;
    }
    menusLoaded.value = false;
    try {
      const loaded = await new MenusService().getAll(restaurantId);
      if (restaurant.value?.id !== restaurantId) return;
      menus.value = loaded.filter((entry) => entry.active);
    } catch (error) {
      // The base menu still works; never another store's menus.
      console.error('Loading the menus failed', error);
      if (restaurant.value?.id === restaurantId) menus.value = [];
    } finally {
      if (restaurant.value?.id === restaurantId) menusLoaded.value = true;
    }
  }
  watch([() => restaurant.value?.id, multipleMenus], () => void loadMenus(), {
    immediate: true,
  });

  const categories = computed<ICategory[]>(() => menu.categories.value ?? []);
  const items = computed<IItem[]>(() => menu.items.value ?? []);

  /** The last derived sections: unchanged ones keep their objects. */
  let previousSections: ICatalogSection[] = [];
  let previousShown: IShownSection[] = [];

  const sections = computed<ICatalogSection[]>(() => {
    previousSections = shareSections(
      groupCatalog(categories.value, items.value, state.menuId),
      previousSections
    );
    return previousSections;
  });
  /** Something to order: two categories, or two products in one. */
  const canReorder = computed(
    () =>
      sections.value.length > 1 ||
      sections.value.some((section) => section.items.length > 1)
  );
  const rows = computed<ICatalogFilterRow[]>(() =>
    sections.value.flatMap((section) =>
      section.items.map((item) => ({ item, category: section.category }))
    )
  );
  const categoryById = computed(
    () => new Map(categories.value.map((category) => [category.id, category]))
  );
  const itemById = computed(() => menu.itemMap.value ?? {});

  const debouncedQuery = refDebounced(
    computed(() => state.query),
    SEARCH_DEBOUNCE_MS
  );
  const searchIndex = computed(() => buildSearchIndex(sections.value));
  const hasQuery = computed(() => splitTerms(debouncedQuery.value).length > 0);
  // The index is built only once something is searched.
  const matcher = computed(() =>
    hasQuery.value
      ? searchMatcher(searchIndex.value, debouncedQuery.value)
      : null
  );

  const filterContext = computed(() => ({ branchId: branchId.value }));
  const counts = computed(() =>
    countCatalogFilters(rows.value, filterContext.value)
  );
  const shownSections = computed<IShownSection[]>(() => {
    previousShown = shareSections(
      filterSections(sections.value, {
        filter: state.filter,
        matcher: matcher.value,
        pinnedIds: state.pinnedIds,
        ctx: filterContext.value,
      }),
      previousShown
    );
    return previousShown;
  });
  const shownCount = computed(() =>
    shownSections.value.reduce((sum, section) => sum + section.matchCount, 0)
  );
  // Not from `matcher`: it reads the search index (every product), and each
  // card asks this per render.
  const isFiltering = computed(() => state.filter !== 'all' || hasQuery.value);

  const rowContext = computed<ICatalogRowContext>(() => ({
    branchId: branchId.value,
    currency: restaurant.value?.currency ?? 'ARS',
    country: restaurant.value?.country ?? 'AR',
  }));

  /** The category a product belongs to (live categories only). */
  function categoryOf(item: IItem): ICategory | null {
    return categoryById.value.get(item.categoryId) ?? null;
  }

  return {
    restaurant,
    branchId,
    storeBranches,
    activeStoreBranches,
    multipleMenus,
    menus,
    menusLoaded,
    reloadMenus: loadMenus,
    categories,
    items,
    sections,
    canReorder,
    rows,
    categoryById,
    itemById,
    debouncedQuery,
    matcher,
    counts,
    shownSections,
    shownCount,
    isFiltering,
    rowContext,
    categoryOf,
    loadStatus: menu.loadStatus,
    catalogRevision: menu.catalogRevision,
    ensureCatalogLoaded: menu.ensureCatalogLoaded,
    reloadCatalog: menu.reloadCatalog,
    refreshItems: menu.refreshItems,
  };
}

export type ICatalogData = ReturnType<typeof useCatalogData>;
